import React, { useMemo } from 'react';
import { Plus, Minus, Calendar, Tag, Lock } from 'lucide-react';
import { ITApplication } from './ITApplicationTypes';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

interface ITApplicationListProps {
    applications: ITApplication[];
    loading: boolean;
    onEdit: (app: ITApplication) => void;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    onWorkflowClick?: (app: ITApplication) => void;
}

const ITApplicationList: React.FC<ITApplicationListProps> = ({
    applications, loading, onEdit, expandedIds, onToggleExpand, onWorkflowClick,
}) => {
    const { formatDate } = useEntitySettings();
    const treeData = useMemo(() => {
        const buildTree = (parentId: string): ITApplication[] => {
            return applications
                .filter(item => item.parentId === parentId || (!item.parentId && parentId === 'root'))
                .map(item => ({
                    ...item,
                    children: buildTree(item.id),
                }));
        };
        return buildTree('root');
    }, [applications]);

    const toggleExpand = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        onToggleExpand(id);
    };

    const ITApplicationRow = ({ node, level = 0 }: { node: ITApplication; level?: number }) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);
        const paddingLeft = level * 24 + 12;

        const getCategoryLabel = () => {
            switch (node.category) {
                case 'APPLICATION': return 'Application';
                case 'MODULE': return 'Module';
                case 'FUNCTION': return 'Function';
                default: return node.category;
            }
        };

        return (
            <React.Fragment>
                <div
                    onClick={() => onEdit(node)}
                    className={`tree-list-row${level > 0 ? ' tree-list-row--child' : ''}`}
                    data-testid={`BTN_it_application_row_${node.id}_open`}
                >
                    <div className="tree-list-cell tree-list-cell--dot"
                         onClick={e => { e.stopPropagation(); onWorkflowClick?.(node); }}
                         title="Workflow — view timeline & act">
                        <ApprovalDot state={node.workflow_state || 'draft'} onClick={() => onWorkflowClick?.(node)} title="Workflow — view timeline & act" />
                    </div>
                    <div className="tree-list-cell tree-list-cell--w-24" data-testid={`TXT_it_application_row_${node.id}_code`}>
                        {node.code ? <span className="cell-code">{node.code}</span> : <span className="text-muted">—</span>}
                    </div>
                    <div
                        className="tree-list-cell tree-list-cell--main"
                        style={{ paddingLeft: `${paddingLeft}px` }}
                    >
                        <button
                            onClick={(e) => toggleExpand(e, node.id)}
                            className={`tree-list-toggle${hasChildren ? '' : ' tree-list-toggle--hidden'}`}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            data-testid={`BTN_it_application_row_${node.id}_toggle`}
                        >
                            {isExpanded ? <Minus size={10} /> : <Plus size={10} />}
                        </button>

                        <div className="tree-list-row-title">
                            <span className="tree-list-row-name cell-name-lock">
                                {node.workflow_state === 'approved' && (
                                    <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                )}
                                {node.name}
                            </span>
                            {node.purpose && (
                                <span className="tree-list-row-sub truncate max-w-[300px]">{node.purpose}</span>
                            )}
                        </div>
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-40">
                        <div className="flex items-center gap-2">
                            <span className="status-badge sm info">
                                {getCategoryLabel()}
                            </span>
                            {node.version && (
                                <span className="text-slate-500 text-xs flex items-center gap-1">
                                    <Tag size={10} /> v{node.version}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-48">
                        {node.category === 'APPLICATION' && (
                            <div className="flex flex-col">
                                <span className="text-slate-700 text-xs font-medium">
                                    {node.type === 'IN_HOUSE' ? 'In-House' :
                                        node.type === 'PURCHASED' ? 'Purchased' : 'Licensed'}
                                </span>
                                {node.vendor && (
                                    <span className="text-slate-500 text-xs mt-0.5">
                                        by {node.vendor}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="tree-list-cell tree-list-cell--w-32">
                        {node.rollOutDate && (
                            <div className="flex items-center gap-1.5">
                                <Calendar size={12} className="text-slate-400" />
                                {formatDate(node.rollOutDate)}
                            </div>
                        )}
                    </div>

                </div>

                <div className={`tree-list-children${isExpanded ? ' tree-list-children--open' : ''}`}>
                    <div className="tree-list-children-inner">
                        {hasChildren && node.children?.map(child => (
                            <ITApplicationRow key={child.id} node={child} level={level + 1} />
                        ))}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    return (
        <div className="tree-list">
            <div className="tree-list-header">
                <div className="tree-list-header-cell tree-list-cell--dot" aria-label="Workflow status"></div>
                <div className="tree-list-header-cell tree-list-cell--w-24">Code</div>
                <div className="tree-list-header-cell tree-list-header-cell--main">IT Component Structure</div>
                <div className="tree-list-header-cell tree-list-cell--w-40">Category / Ver</div>
                <div className="tree-list-header-cell tree-list-cell--w-48">Type / Vendor</div>
                <div className="tree-list-header-cell tree-list-cell--w-32">Roll-out</div>
            </div>

            <div className="tree-list-body">
                {loading ? (
                    <div className="tree-list-body-inner" aria-busy="true" aria-live="polite">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <div key={i} className="tree-list-row tree-list-skeleton-row">
                                <div className="tree-list-cell tree-list-cell--w-24">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--chip" />
                                </div>
                                <div
                                    className="tree-list-cell tree-list-cell--main"
                                    style={{ paddingLeft: `${(i % 3 === 1 ? 36 : 12)}px` }}
                                >
                                    <div className="tree-list-skeleton-toggle" />
                                    <div className="tree-list-row-title">
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--title" />
                                        <div className="tree-list-skeleton-bar tree-list-skeleton-bar--sub" />
                                    </div>
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-40">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--pill" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-48">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--chip" />
                                </div>
                                <div className="tree-list-cell tree-list-cell--w-32">
                                    <div className="tree-list-skeleton-bar tree-list-skeleton-bar--chip" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : applications.length === 0 ? (
                    <NoDataFound
                        title="No IT Applications Found"
                        message="No IT applications have been defined yet. Create one to get started."
                    />
                ) : (
                    <div className="tree-list-body-inner">
                        {treeData.map(node => (
                            <ITApplicationRow key={node.id} node={node} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ITApplicationList;
